import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

export interface LineSeries {
  label: string
  data: number[]
  color: string
  fill?: boolean
  dashed?: boolean
  /** 'left' (default) or 'right' — assigns this series to the secondary Y axis */
  yAxis?: 'left' | 'right'
}

interface Props {
  labels: string[]
  series: LineSeries[]
  height?: number | string
  yTickFormat?: (v: number) => string
  /** Tick formatter for the right (secondary) Y axis */
  y2TickFormat?: (v: number) => string
  yMin?: number
  yMax?: number
  stacked?: boolean
  showLegend?: boolean
}

export function LineChart({
  labels, series, height = 260,
  yTickFormat, y2TickFormat,
  yMin, yMax, stacked = false, showLegend,
}: Props) {
  const legend    = showLegend ?? (series.length > 1)
  const hasRight  = series.some(s => s.yAxis === 'right')

  return (
    <div style={{ height, width: '100%' }}>
      <Line
        data={{
          labels,
          datasets: series.map(s => ({
            label: s.label,
            data: s.data,
            borderColor: s.color,
            backgroundColor: s.fill ? withAlpha(s.color, stacked ? 0.6 : 0.12) : 'transparent',
            tension: 0.4,
            fill: s.fill ? (stacked ? 'origin' : true) : false,
            borderWidth: stacked ? 1 : (s.yAxis === 'right' ? 2 : 2.5),
            pointRadius: stacked ? 0 : (s.yAxis === 'right' ? 2 : 3),
            pointBackgroundColor: s.color,
            borderDash: s.dashed ? [4, 4] : undefined,
            yAxisID: s.yAxis === 'right' ? 'y2' : 'y',
          })),
        }}
        options={{
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: legend
              ? { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, padding: 14, font: { size: 11 } } }
              : { display: false },
            tooltip: {
              callbacks: {
                label: (c) => {
                  const isRight = (c.dataset as { yAxisID?: string }).yAxisID === 'y2'
                  const fmt     = isRight ? y2TickFormat : yTickFormat
                  const y = c.parsed.y ?? 0
                  return c.dataset.label + ': ' + (fmt ? fmt(y) : y.toLocaleString())
                },
              },
            },
          },
          scales: {
            y: {
              min: yMin, max: yMax, stacked,
              position: 'left',
              ticks: { callback: (v) => (yTickFormat ? yTickFormat(Number(v)) : String(v)), font: { family: 'JetBrains Mono' } },
              grid: { color: '#e2e8f0' },
            },
            ...(hasRight ? {
              y2: {
                position: 'right' as const,
                grid: { drawOnChartArea: false },
                ticks: {
                  callback: (v: number | string) => (y2TickFormat ? y2TickFormat(Number(v)) : String(v)),
                  font: { family: 'JetBrains Mono' },
                },
              },
            } : {}),
            x: { stacked, grid: { display: false }, ticks: { font: { weight: 600 } } },
          },
        }}
      />
    </div>
  )
}

function withAlpha(hex: string, a: number) {
  const m = hex.match(/^#([0-9a-f]{6})$/i)
  if (!m) return hex
  const r = parseInt(m[1].slice(0, 2), 16), g = parseInt(m[1].slice(2, 4), 16), b = parseInt(m[1].slice(4, 6), 16)
  return `rgba(${r},${g},${b},${a})`
}
